import { DEFAULT_PASSWORD_POLICY, PasswordPolicy } from "../../interfaces/admin/tenant.interface";

export interface PasswordRuleCheck {
    label: string;
    ok: boolean;
}

/**
 * The tenant's password rules applied to a candidate password, one row per
 * rule (tenant_authentication_plan.md Phase 3). Display and pre-validation
 * only: Firebase enforces the same rules on sign-up, reset and change.
 */
export const checkPasswordRules = (
    policy: PasswordPolicy | null | undefined,
    password: string
): PasswordRuleCheck[] => {
    const p = policy ?? DEFAULT_PASSWORD_POLICY;
    const checks: PasswordRuleCheck[] = [
        { label: `At least ${p.minLength} characters`, ok: password.length >= p.minLength }
    ];
    if (p.requireUppercase)
        checks.push({ label: "An uppercase letter", ok: /[A-Z]/.test(password) });
    if (p.requireLowercase)
        checks.push({ label: "A lowercase letter", ok: /[a-z]/.test(password) });
    if (p.requireNumeric) checks.push({ label: "A number", ok: /[0-9]/.test(password) });
    if (p.requireSymbol)
        checks.push({ label: "A symbol (such as ! or #)", ok: /[^A-Za-z0-9]/.test(password) });
    return checks;
};

export const passwordMeetsRules = (
    policy: PasswordPolicy | null | undefined,
    password: string
): boolean => checkPasswordRules(policy, password).every(c => c.ok);

/** Whether the tenant asks for more than Firebase's built-in 6 characters. */
export const hasCustomPasswordRules = (policy: PasswordPolicy | null | undefined): boolean =>
    !!policy &&
    (policy.minLength !== DEFAULT_PASSWORD_POLICY.minLength ||
        policy.requireUppercase ||
        policy.requireLowercase ||
        policy.requireNumeric ||
        policy.requireSymbol);
